import React from "react";
import { HStack, IconButton, Icon, Text } from "@astryxdesign/core";
import { GripVertical, X } from "lucide-react";
import styles from "./NavRailItem.module.css";

export interface NavRailItemProps {
  /** Row text — truncates with an ellipsis rather than wrapping. */
  label: string;
  /**
   * Drag-and-drop wiring — a sortable library's `listeners` + `attributes`
   * (e.g. dnd-kit's `useSortable`), spread together onto the row root, so
   * the *entire* row is the drag surface rather than a dedicated grip icon.
   * Typed as plain `HTMLAttributes` (not a sortable-library-specific type)
   * so this package stays free of a drag-library dependency — the caller
   * owns that. Omit for rows that aren't sortable (e.g. the read-only
   * rail): they get no cursor/hover change and no grip icon, and behave
   * exactly as before.
   *
   * When present, a passive `GripVertical` icon renders at the row start
   * as a visual drag indicator — not an `IconButton` or any other
   * interactive control, so it never captures its own hover/click; the
   * *row* is the drag surface and the hover target (see `.draggable` in
   * the CSS module).
   */
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  onClick?: () => void;
  /** Section row currently in view — renders a subtle accent background. */
  isActive?: boolean;
  /** Question rows nest under their section with extra start padding. */
  isIndented?: boolean;
  /**
   * When provided, renders a delete (X) control, hidden by default and
   * revealed on row hover or keyboard focus within the row.
   */
  onDelete?: () => void;
  deleteLabel?: string;
}

/**
 * Presentational row for the NavRail's expanded panel — a section or a
 * nested question. Styling only: the drag wiring (via `dragHandleProps`)
 * and click/delete handlers are wired by the caller.
 *
 * Text renders via Astryx's `Text` (not hand-rolled CSS), so this MUST be
 * rendered inside an Astryx <Theme> scope.
 */
const NavRailItem = ({
  label,
  dragHandleProps,
  onClick,
  isActive = false,
  isIndented = false,
  onDelete,
  deleteLabel,
}: NavRailItemProps): React.ReactElement => {
  const rowClasses = [
    styles.row,
    isActive && styles.active,
    isIndented && styles.indented,
    dragHandleProps && styles.draggable,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <HStack align="center" gap={1} className={rowClasses} {...dragHandleProps}>
      {dragHandleProps && (
        <div className={styles.grip}>
          <Icon icon={GripVertical} color="secondary" />
        </div>
      )}
      <button type="button" className={styles.label} onClick={onClick}>
        <Text type="supporting" color="primary" maxLines={1}>
          {label}
        </Text>
      </button>
      {onDelete && (
        <div className={styles.delete}>
          <IconButton
            icon={<Icon icon={X} color="secondary" />}
            label={deleteLabel ?? "Delete"}
            variant="ghost"
            size="sm"
            onClick={onDelete}
          />
        </div>
      )}
    </HStack>
  );
};

export default NavRailItem;
